import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FaturasBeeKpi {
  hoje: number;
  ontem: number;
  ultimos7: number;
  /** CNPJs da carteira que receberam a fatura por e-mail na competência do ciclo. */
  entreguesMes: number;
  /** Tamanho da carteira do ciclo (última importação da Claro). */
  carteira: number;
  /** entreguesMes ÷ carteira, em %. */
  pctMes: number;
}

/**
 * KPI de faturas do header.
 *
 * Duas coisas diferentes, e a segunda é a que importa (Douglas 17/08): o Bee
 * DEVOLVER a fatura pra nós (`hoje/ontem/ultimos7`, de `fatura_upload`) não é o
 * mesmo que ela CHEGAR no cliente. "Não adianta saber quantos estamos baixando,
 * mas quantas estamos enviando para o e-mail do cliente mesmo."
 *
 * Medido em 17/08: baixamos de 4.257 CNPJs no mês e enviamos e-mail para 2.177.
 *
 * Fonte: RPC `kpi_faturas_bee` (fuso America/Sao_Paulo). O `entregues_mes ÷
 * carteira` bate exato com o painel Ciclo de Faturas, que é para onde o chip
 * navega.
 */
/** Uma linha de `kpi_faturas_bee`, como o banco devolve (snake_case). */
interface LinhaKpi {
  hoje?: number;
  ontem?: number;
  ultimos7?: number;
  entregues_mes?: number;
  carteira?: number;
}

export function useFaturasBeeKpi() {
  return useQuery<FaturasBeeKpi>({
    queryKey: ['faturas-bee-kpi'],
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: async () => {
      // `kpi_faturas_bee` não está no mapa gerado de tipos (integrations/supabase/
      // types.ts), então o generic não conhece nem o nome da RPC nem as colunas.
      // Mesmo caminho que o painel Ciclo de Faturas já usa (`ciclo_faturas_painel`):
      // a forma é declarada aqui. Sem isto, cada campo vira um erro de typecheck.
      const { data, error } = await (supabase as unknown as {
        rpc: (fn: string) => Promise<{ data: LinhaKpi[] | LinhaKpi | null; error: unknown }>;
      }).rpc('kpi_faturas_bee');
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) ?? null;
      const entreguesMes = row?.entregues_mes ?? 0;
      const carteira = row?.carteira ?? 0;
      return {
        hoje: row?.hoje ?? 0,
        ontem: row?.ontem ?? 0,
        ultimos7: row?.ultimos7 ?? 0,
        entreguesMes,
        carteira,
        pctMes: carteira > 0 ? Math.round((entreguesMes / carteira) * 1000) / 10 : 0,
      };
    },
  });
}
